<script setup lang="ts">
import { ref, reactive } from "vue";
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

// Minimal local type so this component stays type-safe without importing from `DataTable.vue`.
interface ColumnDef {
  key: string
  label: string
  type?: string
  [key: string]: any
}

const props = withDefaults(defineProps<{
  columns: ColumnDef[]
  label: string
  logo?: string | null
  enableSearch?: boolean
  enableAdd?: boolean
  enableEdit?: boolean
  enableDetail?: boolean
  enableDelete?: boolean
}>(), {
  logo: null,
  enableSearch: false,
  enableAdd: true,
  enableEdit: true,
  enableDetail: true,
  enableDelete: true,
})

interface Tab {
  id: string;
  label: string;
  content: any;
  rowId?: number;
}

const tabs = reactive<Tab[]>([
  { id: "datatable", label: "Data Table", content: "datatable" },
]);

const activeTab = ref("datatable");

const openRowTab = (rowId: number, label) => {
	console.log("Child did something with row", rowId);
  const tabId = `row-${rowId}`;
  if (!tabs.find(t => t.id === tabId)) {
    tabs.push({
      id: tabId,
	  rowId: rowId,
      label: label,
      content: rowId,
    });
  }
  activeTab.value = tabId;
};

const closeTab = (tabId: string) => {
  const idx = tabs.findIndex(t => t.id === tabId);
  if (idx !== -1) {
    tabs.splice(idx, 1);
    if (activeTab.value === tabId) {
      activeTab.value = tabs[0].id;
    }
  }
};

defineExpose({ openRowTab });

</script>

<template>
  <div class="flex flex-col h-full">
    <!-- Tab headers -->
    <div class="flex border-b">
      <div
        v-for="tab in tabs"
        :key="tab.id"
        :class="['px-4 py-2 cursor-pointer flex items-center gap-2', { 'border-b-2 border-blue-500 font-bold': activeTab === tab.id }]"
        @click="activeTab = tab.id"
      >
	    <slot v-if="tab.id === 'datatable'" name="logo" />
		<span v-if="tab.id === 'datatable'">{{t(props.label)}} </span>
		<span v-if="tab.id !== 'datatable'">{{tab.label}} </span>
        <span v-if="tab.id !== 'datatable'" @click.stop="closeTab(tab.id)" class="text-gray-400 hover:text-gray-700 cursor-pointer">&times;</span>
      </div>
    </div>

    <!-- Tab contents -->
    <div class="flex-1 overflow-auto p-4">
      <div v-for="tab in tabs" :key="tab.id" v-show="activeTab === tab.id" class="h-full">
        <template v-if="tab.content === 'datatable'">
		  <slot name="maincontent" />
        </template>
        <template v-else>
            <!-- Important: pass the rowId belonging to *this* tab, not the globally active tab -->
            <slot name="tabbedcontent" :row="tab.rowId" />
		    </template>
      </div>
    </div>
  </div>
</template>
